import React, { useCallback, useEffect, useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, List, ListItemButton, MenuItem, Select, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ClientService from '../../services/ClientService'
import M1AdvisorService from '../../services/M1AdvisorService'
import GlobalTip from '../../providers/GlobalTip'
import SectionTitle from '../../components/common/SectionTitle'

const EMPTY = {
  first_name: '',
  last_name: '',
  short_description: '',
  long_description: '',
  status: 'active',
  avatar: null,
  remove_avatar: false,
  client_ids: [],
}

function advisorName(advisor) {
  return [advisor?.first_name, advisor?.last_name].filter(Boolean).join(' ')
}

function clientLabel(client) {
  return [client?.ClientCode ? `[${client.ClientCode}]` : '', client?.Name, client?.Id ? `(#${client.Id})` : ''].filter(Boolean).join(' ')
}

function AdvisorClientSelector({ clients, selectedClientIds, onChange }) {
  const [availableSelection, setAvailableSelection] = useState([])
  const [assignedSelection, setAssignedSelection] = useState([])
  const selectedIds = new Set(selectedClientIds.map(Number))
  const availableClients = clients.filter((client) => !selectedIds.has(Number(client.Id)))
  const assignedClients = clients.filter((client) => selectedIds.has(Number(client.Id)))
  const moveToAssigned = () => {
    onChange([...new Set([...selectedClientIds.map(Number), ...availableSelection])])
    setAvailableSelection([])
  }
  const moveToAvailable = () => {
    const removedIds = new Set(assignedSelection)
    onChange(selectedClientIds.map(Number).filter((id) => !removedIds.has(id)))
    setAssignedSelection([])
  }
  const renderList = (items, selection, setSelection, emptyLabel) => <List dense sx={{ height: 180, overflow: 'auto', border: '1px solid #d0d5dd', borderRadius: 1, p: 0.5 }}>
    {items.length === 0 && <Typography sx={{ p: 1 }} variant="caption" color="text.secondary">{emptyLabel}</Typography>}
    {items.map((client) => {
      const id = Number(client.Id)
      const selected = selection.includes(id)
      return <ListItemButton key={id} selected={selected} onClick={() => setSelection(selected ? selection.filter((value) => value !== id) : [...selection, id])}>
        <Typography variant="body2" noWrap>{clientLabel(client)}</Typography>
      </ListItemButton>
    })}
  </List>

  return <Box>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>Assigned clients</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', gap: 1, alignItems: 'center' }}>
      <Box><Typography variant="caption" color="text.secondary">Available clients</Typography>{renderList(availableClients, availableSelection, setAvailableSelection, 'No unassigned clients')}</Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <IconButton aria-label="Assign selected clients" onClick={moveToAssigned} disabled={availableSelection.length === 0}><ChevronRightIcon /></IconButton>
        <IconButton aria-label="Unassign selected clients" onClick={moveToAvailable} disabled={assignedSelection.length === 0}><ChevronLeftIcon /></IconButton>
      </Box>
      <Box><Typography variant="caption" color="text.secondary">Assigned to this advisor</Typography>{renderList(assignedClients, assignedSelection, setAssignedSelection, 'No assigned clients')}</Box>
    </Box>
  </Box>
}

function AdvisorDialog({ open, initial, clients, onClose, onSaved }) {
  const tip = GlobalTip.useMessage()
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const editing = Boolean(initial?.id)

  useEffect(() => {
    setForm(initial ? {
      first_name: initial.first_name || '',
      last_name: initial.last_name || '',
      short_description: initial.short_description || '',
      long_description: initial.long_description || '',
      status: initial.status || 'active',
      avatar: null,
      remove_avatar: false,
      client_ids: Array.isArray(initial.clients) ? initial.clients.map((client) => Number(client.Id)) : [],
    } : EMPTY)
  }, [initial, open])

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.short_description.trim() || !form.long_description.trim()) {
      tip.show('First name, last name, short description, and long description are required', 'error')
      return
    }

    const payload = new FormData()
    ;['first_name', 'last_name', 'short_description', 'long_description', 'status'].forEach((field) => payload.append(field, form[field]))
    form.client_ids.forEach((clientId) => payload.append('client_ids[]', String(clientId)))
    if (form.avatar) payload.append('avatar', form.avatar)
    if (form.remove_avatar) payload.append('remove_avatar', 'true')

    setSaving(true)
    try {
      if (editing) await M1AdvisorService.update(initial.id, payload)
      else await M1AdvisorService.create(payload)
      tip.show(editing ? 'M1 Advisor updated' : 'M1 Advisor created', 'success')
      onSaved()
    } catch (error) {
      tip.show(error?.response?.data?.message || 'Unable to save M1 Advisor', 'error')
    } finally {
      setSaving(false)
    }
  }

  const avatarPreview = form.avatar ? URL.createObjectURL(form.avatar) : initial?.avatar_url

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>{editing ? 'Edit' : 'New'} M1 Advisor</DialogTitle>
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '18px !important' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar src={avatarPreview || undefined} sx={{ width: 64, height: 64 }}>{!avatarPreview && advisorName(form).slice(0, 2).toUpperCase()}</Avatar>
        <Button component="label" variant="outlined">Upload avatar<input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setForm((current) => ({ ...current, avatar: event.target.files?.[0] || null, remove_avatar: false }))} /></Button>
        {avatarPreview && <Button color="error" variant="outlined" onClick={() => setForm((current) => ({ ...current, avatar: null, remove_avatar: true }))}>Remove avatar</Button>}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
        <TextField label="First name" value={form.first_name} onChange={(event) => setForm((current) => ({ ...current, first_name: event.target.value }))} required fullWidth />
        <TextField label="Last name" value={form.last_name} onChange={(event) => setForm((current) => ({ ...current, last_name: event.target.value }))} required fullWidth />
      </Box>
      <TextField label="Short description" value={form.short_description} onChange={(event) => setForm((current) => ({ ...current, short_description: event.target.value }))} required fullWidth inputProps={{ maxLength: 500 }} helperText={`${form.short_description.length}/500`} />
      <TextField label="Long description" value={form.long_description} onChange={(event) => setForm((current) => ({ ...current, long_description: event.target.value }))} required fullWidth multiline minRows={5} inputProps={{ maxLength: 5000 }} helperText={`${form.long_description.length}/5000`} />
      <AdvisorClientSelector clients={clients} selectedClientIds={form.client_ids} onChange={(clientIds) => setForm((current) => ({ ...current, client_ids: clientIds }))} />
      <Select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))} size="small" aria-label="M1 Advisor status">
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="inactive">Inactive</MenuItem>
      </Select>
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Cancel</Button><Button onClick={save} variant="contained" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button></DialogActions>
  </Dialog>
}

export default function M1AdvisorsManagement() {
  const tip = GlobalTip.useMessage()
  const [items, setItems] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogItem, setDialogItem] = useState(undefined)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setItems(await M1AdvisorService.list())
    } catch (error) {
      tip.show('Unable to load M1 Advisors', 'error')
    } finally {
      setLoading(false)
    }
  }, [tip])

  useEffect(() => { load() }, [load])
  useEffect(() => { ClientService.listClientsSimple().then((result) => setClients(Array.isArray(result) ? result : [])).catch(() => setClients([])) }, [])

  const remove = async (advisor) => {
    if (!window.confirm(`Delete "${advisorName(advisor)}"?`)) return
    try {
      await M1AdvisorService.remove(advisor.id)
      tip.show('M1 Advisor deleted', 'success')
      load()
    } catch (error) {
      tip.show('Unable to delete M1 Advisor', 'error')
    }
  }

  return <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
    <SectionTitle title="M1 Advisors" subtitle="Manage the advisor profiles shown on the V3 client dashboard." />
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialogItem(null)}>New M1 Advisor</Button>
    </Box>
    {loading ? <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box> : <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
      {items.length === 0 && <Typography color="text.secondary">No M1 Advisors have been created.</Typography>}
      {items.map((advisor) => <Box key={advisor.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, bgcolor: '#fff', display: 'flex', gap: 2 }}>
        <Avatar src={advisor.avatar_url || undefined} sx={{ width: 56, height: 56 }}>{advisorName(advisor).slice(0, 2).toUpperCase()}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}><Typography fontWeight={700}>{advisorName(advisor)}</Typography><Chip size="small" label={advisor.status} color={advisor.status === 'active' ? 'success' : 'default'} /></Box>
          <Typography sx={{ mt: 0.5 }} variant="body2" color="text.secondary">{advisor.short_description}</Typography>
          <Typography sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }} variant="body2">{advisor.long_description}</Typography>
        </Box>
        <IconButton aria-label={`Edit ${advisorName(advisor)}`} onClick={() => setDialogItem(advisor)}><EditIcon /></IconButton>
        <IconButton aria-label={`Delete ${advisorName(advisor)}`} color="error" onClick={() => remove(advisor)}><DeleteIcon /></IconButton>
      </Box>)}
    </Box>}
    <AdvisorDialog open={dialogItem !== undefined} initial={dialogItem} clients={clients} onClose={() => setDialogItem(undefined)} onSaved={() => { setDialogItem(undefined); load() }} />
  </Box>
}

